// Shapes and status palettes for the Health Monitor table (DashboardPage).
//
// They live outside the page so the row renderers can be split into their own
// module without either side owning a private copy of the colour map: two
// copies do not stay equal, and the same server then reads as two different
// severities depending on which cell you look at.
//
// For the same reason the palettes are NOT defined here any more: they are
// re-exported from components/health/statusVisuals, which is the project's one
// status-colour source and is also what the drill-in pages render from. A
// second literal copy of the same hexes is how the list and the detail page
// drift into disagreeing about what "DEGRADED" looks like.

import type { CSSProperties } from 'react';

export { STATUS_COLORS, SPARKLINE_COLORS } from './health/statusVisuals';

export interface HistoryEntry {
  timestamp: number;
  status: string;
}

export interface NetworkStatus {
  ip: string;
  state: string;
  staleSince?: string;
}

export interface StepResult {
  name: string;
  success: boolean;
  severity?: 'warn' | 'info';
  latency: number;
  error?: string;
  notes?: string[];
}

export interface ProxyStatus {
  protocol: string;
  host: string;
  port: number;
  state: string;
  latency: number;
  totalDuration: number;
  steps: StepResult[];
  label?: string;
  country?: string;
  staleSince?: string;
}

export interface HealthServer {
  key: string;
  name: string;
  mainIP: string;
  networks: string[];
  checkMode: string;
  type?: 'dedicated' | 'mobile-gateway';
  country: string;
  tags: string[];
  enabled: boolean;
  status: string;
  lastCheck: string | null;
  pingLatency: number | null;
  packetLoss: number | null;
  visibility: number | null;
  visibilityChecked: boolean | null;
  networkHistory: HistoryEntry[];
  proxyHistory: HistoryEntry[];
  networkStatuses: NetworkStatus[];
  proxyStatuses: ProxyStatus[];
}

// Higher = worse, used for default sort (problems first). STALE sorts below
// HEALTHY because operationally it is "dead but already acknowledged" — we
// want fresh outages above and known-dead targets at the bottom of the list.
export const STATUS_SEVERITY: Record<string, number> = {
  DOWN: 7,
  ERROR: 6,
  ZOMBIE: 5,
  DEGRADED: 4,
  SUSPECT: 3,
  UNKNOWN: 2,
  HEALTHY: 1,
  STALE: 0,
};

// One line of chips, ~18px tall, never wrapping mid-identifier. A chip that
// cannot fit its column is clipped with an ellipsis and keeps the full value
// in its tooltip — a wrapped host or port doubles the row height and reads as
// two different addresses.
//
// 12px, not 11px: a chip reading "SOCKS5 :1080 DOWN" is the payload of this
// screen, not a footnote, and 12px is the floor for anything meant to be read.
export const CHIP_STYLE: CSSProperties = {
  fontSize: 12,
  lineHeight: '18px',
  padding: '0 5px',
  margin: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Rows must stay one screen-line of primary content, so each chip column keeps
// at most two lines: the worst states are rendered and the tail collapses into
// a "+N" chip that carries the breakdown in its tooltip.
export const MAX_NETWORK_CHIPS = 2;
export const MAX_PROXY_CHIPS = 4;

// How many checks the History strip draws. The endpoint hands back up to 100;
// the column is ~116px wide and now spends part of that on a written-out row
// label ("Net" / "Proxy" / "Modem") instead of a bare letter, so the strip gets
// 12 bars. The rest is one click away on the drill-in page.
export const HISTORY_BARS = 12;

/** Sorts a copy worst-state-first, keeping the original order within a state. */
export function bySeverity<T>(items: T[], state: (item: T) => string): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const sa = STATUS_SEVERITY[state(a.item)] || 0;
      const sb = STATUS_SEVERITY[state(b.item)] || 0;
      return sa !== sb ? sb - sa : a.index - b.index;
    })
    .map((entry) => entry.item);
}

/** "3 × DOWN" style counts for the overflow tooltip. */
export function countStates(states: string[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const state of states) counts[state] = (counts[state] || 0) + 1;
  return counts;
}
